// Panel de retroalimentacion al glosario (Fase 42).
//
// Muestra las correcciones manuales que el usuario hizo (kind === "edit")
// con toggles pre-tickeados para agregarlas como terminos al glosario.
// Solo aparece cuando hay al menos un edit entre las decisiones.

import { useMemo } from "react";
import styled from "styled-components";
import type { CorrectionDecision } from "../../state/atoms";
import type { AppliedCorrection, PendingCorrection } from "../../platform/api";

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Panel = styled.div`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.color.accent};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.accentMuted};
  margin-bottom: 16px;
`;

const PanelTitle = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin-bottom: 8px;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const PanelDescription = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin-bottom: 12px;
`;

const Row = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  &:hover {
    background: ${({ theme }) => theme.color.background}44;
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: ${({ theme }) => theme.color.accent};
`;

const TermText = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Arrow = styled.span`
  color: ${({ theme }) => theme.color.textDim};
  margin: 0 4px;
`;

const OriginalText = styled.span`
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.xs};
  text-decoration: line-through;
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  text-align: center;
  padding: 8px;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type EditItem = {
  id: string;
  correctedText: string;
  originalText: string;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  decisions: Map<string, CorrectionDecision>;
  pendingItems: PendingCorrection[];
  appliedItems: AppliedCorrection[];
  selectedIds: Set<string>;
  onToggle: (itemId: string, included: boolean) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GlossaryFeedbackPanel({
  decisions,
  pendingItems,
  appliedItems,
  selectedIds,
  onToggle,
}: Props) {
  const editItems = useMemo((): EditItem[] => {
    const result: EditItem[] = [];
    const all = [...pendingItems, ...appliedItems];

    for (const item of all) {
      const decision = decisions.get(item.id);
      if (decision?.kind === "edit" && decision.correctedText) {
        result.push({
          id: item.id,
          correctedText: decision.correctedText,
          originalText: item.before,
        });
      }
    }
    return result;
  }, [decisions, pendingItems, appliedItems]);

  if (editItems.length === 0) {
    return (
      <Panel>
        <PanelTitle>Retroalimentar al glosario</PanelTitle>
        <EmptyText>
          No hay correcciones manuales para agregar al glosario.
        </EmptyText>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelTitle>Retroalimentar al glosario</PanelTitle>
      <PanelDescription>
        Las correcciones que escribiste manualmente se ofrecen como nuevos
        términos del glosario. La IA las usará en futuros pipelines. Podés
        destildear las que no quieras agregar.
      </PanelDescription>

      {editItems.map((edit) => (
        <Row key={edit.id}>
          <Checkbox
            type="checkbox"
            checked={selectedIds.has(edit.id)}
            onChange={(e) => onToggle(edit.id, e.target.checked)}
          />
          <TermText>{edit.correctedText}</TermText>
          <Arrow>←</Arrow>
          <OriginalText>{edit.originalText}</OriginalText>
        </Row>
      ))}
    </Panel>
  );
}
